import React, { Suspense, useEffect, useRef, useState } from 'react';
import {
  classes,
  type GlobalProps,
  OutlinedButton,
} from '@bloomberg/media-ui-core';
import { Apple, Facebook, Terminal, Twitter } from '@bloomberg/media-ui-icons';
import RegUIClient, {
  CaptchaType,
  RegUIClientLoginResponse,
} from 'reg-ui-client';

import { shouldAutoOptIn } from '../EmailForm';
import { EmailVerificationForm } from '../EmailVerificationForm';
import { GoogleResponse, GoogleSignInButton } from '../GoogleSignInButton';
import { defaultMessages, LABEL_KEYS, useTranslateMessage } from '../i18n';
import { OrSeparator } from '../OrSeparator';
import { RegGoogleTracking, RegNewRelicLogging } from '../utils/AnalyticsUtils';
import { LoginRegistrationErrors } from '../utils/interfaces';
import { shouldRedirectToPostLoginConversion } from '../utils/postLoginConversion';
import {
  onBconnectSignIn,
  onGoogleSignIn,
  onSocialLogIn,
  SOCIAL_SITES,
  SupportedSocialsType,
} from '../utils/regUIHelpers';
import { isOnboardingPath } from '../utils/utils';

import styles from './styles.module.css';

const GAMES_ASSET_BASE_URL = 'https://assets.bwbx.io/s3/reg2/images';

type GameType = 'alphadots' | 'pointed';

const LazyDotLottie = React.lazy(() =>
  import('@lottiefiles/dotlottie-react').then((mod) => ({
    default: mod.DotLottieReact,
  }))
);

function getGameFromPath(pathname: string): GameType | undefined {
  if (pathname.endsWith('/games/alphadots')) return 'alphadots';
  if (pathname.endsWith('/games/pointed')) return 'pointed';

  return undefined;
}

export type Props = GlobalProps & {
  socialLoginsList: SupportedSocialsType[];
  setUserDataResp: (userDataResp: RegUIClientLoginResponse) => void;
  regUIClient?: RegUIClient;

  callerAppInfo: {
    // Trigger configuration string in Google Tag Manager, eg "embedded_checkout"
    // E.g. here, but needs consolidation:
    // https://bbgithub.dev.bloomberg.com/Consumer-Web/fortress-client/blob/main/packages/fortress-client/src/constants/gtm.ts#L11
    triggerEventName: string;
    // Backend application caller, should be added here
    // https://bbgithub.dev.bloomberg.com/Consumer-Web/reg-ui-client/blob/main/lib/interfaces.ts#L35
    applicationCaller: string;
  };
  onEmailCapture?: (email: string, optIn: boolean, success?: boolean) => void;

  formTitle?: string;
  showTitle?: boolean;
  inlineContinueButton?: boolean;
  theme?: 'light' | 'dark';
  countryCode?: string;
  allowEditEmail?: boolean;
  continueButtonText?: string;
  onGoogleSignIn?: (googleResponse: GoogleResponse) => Promise<void>;
  onClickForGoogle?: () => void;
  onTerminalLogin?: (email: string, success: boolean) => void;
  emailFocus?: boolean;
  allowPassword?: boolean;
  enableOnboarding?: boolean;
  leadSource?: string;
  isGiftedArticleUser?: boolean;
};

export const LogInRegistration = React.forwardRef<HTMLInputElement, Props>(
  function LogInRegistration(props: Props, ref) {
    const translateMessage = useTranslateMessage();
    const {
      socialLoginsList = [
        SOCIAL_SITES.GOOGLE,
        SOCIAL_SITES.APPLE,
        SOCIAL_SITES.TERMINAL,
      ] as SupportedSocialsType[],

      setUserDataResp,

      formTitle = translateMessage({
        id: LABEL_KEYS['media-ui-login_signInOrCreateAccount'],
        defaultMessage: defaultMessages['media-ui-login_signInOrCreateAccount'],
      }),
      showTitle = true,
      inlineContinueButton = false,
      theme = 'light',
      countryCode = undefined,
      allowEditEmail = true,
      continueButtonText = translateMessage({
        id: LABEL_KEYS['media-ui-login_continue'],
        defaultMessage: defaultMessages['media-ui-login_continue'],
      }),

      onEmailCapture,
      onClickForGoogle,
      onTerminalLogin = () => {},

      callerAppInfo,

      emailFocus = true,
      allowPassword = false,
      leadSource,
      enableOnboarding = false,
      isGiftedArticleUser,
    } = props;

    /* c8 ignore next */
    if (!props.regUIClient) {
      // eslint-disable-next-line no-console
      console.warn(
        'Please pass in a regUIClient parameter from the client instead!'
      );
    }

    const [game, setGame] = useState<GameType | undefined>(undefined);

    useEffect(() => {
      setGame(getGameFromPath(window.location.pathname));
    }, []);

    /* c8 ignore next */
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    /* c8 ignore next */
    const [showSocialSignins, setShowSocialSignins] = useState<boolean>(true);
    /* c8 ignore next */
    const [error, setError] = useState<LoginRegistrationErrors>({});

    // We instantiate GoogleTracking/NewRelicLogging in a useState hook since it adds events to the window and
    // we don't want that to be attempted server side.
    /* c8 ignore next */
    const [googleTracking, setGoogleTracking] = useState<RegGoogleTracking>();
    /* c8 ignore next */
    const [nr, setNR] = useState<RegNewRelicLogging>();

    // Because of closure, we cannot use this value directly in the social functions
    // below, we need to use the ref defined below that mirrors this state value
    // https://stackoverflow.com/questions/72956844/why-is-react-state-not-updated-inside-functions
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false);
    /* c8 ignore next */
    const [regUIClient, setRegUIClient] = useState<RegUIClient | undefined>(
      props.regUIClient
    );
    const marketingConsentRef = useRef(marketingConsent);

    /* c8 ignore next */
    const [loginResp, setLoginResp] = useState<RegUIClientLoginResponse>();

    /* c8 ignore next */
    useEffect(() => {
      if (regUIClient) {
        return;
      }

      setRegUIClient(new RegUIClient());
    }, []);

    /* c8 ignore next */
    useEffect(() => {
      setMarketingConsent(shouldAutoOptIn(countryCode));
    }, [countryCode]);

    /* c8 ignore next */
    useEffect(() => {
      marketingConsentRef.current = marketingConsent;
    }, [marketingConsent]);

    useEffect(() => {
      setGoogleTracking(new RegGoogleTracking(callerAppInfo.triggerEventName));
      setNR(new RegNewRelicLogging(callerAppInfo.triggerEventName));
    }, []);

    /* c8 ignore next */
    useEffect(() => {
      if (loginResp) {
        setUserDataResp(loginResp);

        if (enableOnboarding && loginResp?.success) {
          let newURL = `${window.location.origin}/account/onboarding`;
          const redirectTo = isOnboardingPath(window.location.pathname)
            ? undefined
            : `redirectTo=${encodeURIComponent(window.location.href)}`;
          let shouldRedirect = false;

          if (loginResp.data.redirectToPostLoginConversion) {
            newURL += `?isReturningUser=true${
              redirectTo ? `&${redirectTo}` : ''
            }`;
            shouldRedirect = true;
          }

          if (
            loginResp.data.firstTimeUser ||
            loginResp.data.accountWasCreated
          ) {
            // only new users go to onboarding
            shouldRedirect = true;
            newURL += redirectTo ? `?${redirectTo}` : '';
          }

          if (shouldRedirect) {
            window.location.href = newURL;
          }
        }
      }
    }, [loginResp]);

    // Gifted article users are not redirected to post-login conversion
    const postLoginConversionEnabled = !isGiftedArticleUser;

    const mapSocialsToIcons = {
      [SOCIAL_SITES.APPLE]: <Apple />,
      [SOCIAL_SITES.FACEBOOK]: <Facebook className={styles.facebook} />,
      [SOCIAL_SITES.TWITTER]: <Twitter className={styles.twitter} />,
      [SOCIAL_SITES.TERMINAL]: <Terminal />,
    };

    const emailSignInFunc = (respInfo: RegUIClientLoginResponse) => {
      if (!regUIClient) return;

      setLoginResp({
        ...respInfo,
        data: {
          ...respInfo.data,
          redirectToPostLoginConversion: shouldRedirectToPostLoginConversion(
            (regUIClient.getSubscriberData()?.subscriptionsData || []).length >
              0,
            // not a first time user is a returning user
            !respInfo.data.firstTimeUser,
            postLoginConversionEnabled
          ),
        },
      });
    };

    const googleSignInFunc = async (resp: GoogleResponse) => {
      if (regUIClient) {
        const googleUserDataResp = await onGoogleSignIn(
          regUIClient,
          resp,
          marketingConsentRef.current
        );

        onEmailCapture?.(
          (googleUserDataResp.data?.email as string) || '',
          marketingConsentRef.current,
          googleUserDataResp.success
        );

        if (googleUserDataResp.success) {
          /* c8 ignore next */
          googleTracking?.trackSocialLogIn(
            SOCIAL_SITES.GOOGLE,
            googleUserDataResp.data.accountWasCreated as unknown as boolean,
            googleUserDataResp.data.accountWasLinked as unknown as boolean
          );

          /* c8 ignore next */
          nr?.logSocialLogIn(
            SOCIAL_SITES.GOOGLE,
            googleUserDataResp.data.accountWasCreated as unknown as boolean,
            googleUserDataResp.data.accountWasLinked as unknown as boolean
          );
        } else {
          const errorMessage =
            googleUserDataResp.error?.errorMessage ||
            translateMessage(
              {
                id: LABEL_KEYS['media-ui-login_errorSigningInWithSocial'],
                defaultMessage:
                  defaultMessages['media-ui-login_errorSigningInWithSocial'],
              },
              { socialSite: SOCIAL_SITES.GOOGLE }
            );

          if (!googleUserDataResp.error) {
            googleUserDataResp.error = {
              name: errorMessage,
              message: errorMessage,
              errorMessage,
            };
          }

          googleUserDataResp.error.errorMessage = errorMessage;
          setError({ ...error, social: errorMessage });

          /* c8 ignore next */
          googleTracking?.trackErrorEvent(`error_${SOCIAL_SITES.GOOGLE}`);
          /* c8 ignore next */
          nr?.logErrorEvent('MediaUiLoginErrors', {
            errorMessage:
              googleUserDataResp.error?.errorMessage ||
              defaultMessages['media-ui-login_errorSigningInWithSocial'],
            email: (googleUserDataResp.data?.email as string) || '',
            type: 'GOOGLE',
          });
        }

        setLoginResp({
          ...googleUserDataResp,
          data: {
            ...googleUserDataResp.data,
            redirectToPostLoginConversion: shouldRedirectToPostLoginConversion(
              (regUIClient.getSubscriberData()?.subscriptionsData || [])
                .length > 0,
              // if account was not created, then it's a returning user
              !googleUserDataResp.data.accountWasCreated,
              postLoginConversionEnabled
            ),
          },
        });
        setIsLoggedIn(googleUserDataResp.success);
      }
      if (props.onGoogleSignIn) {
        await props.onGoogleSignIn(resp);
      }
    };

    /* c8 ignore next */
    const socialSignInFunc = async (
      socialSite: SupportedSocialsType
    ): Promise<void> => {
      /* c8 ignore next 3 */
      if (!regUIClient) {
        return;
      }

      /* c8 ignore next */
      googleTracking?.trackClickEvent(`connect_${socialSite}`);
      /* c8 ignore next */
      nr?.logClickEvent(`connect_${socialSite}`);

      const socialUserDataResp = await onSocialLogIn(
        regUIClient,
        socialSite,
        marketingConsent,
        leadSource
      );

      onEmailCapture?.(
        (socialUserDataResp.data?.email as string) || '',
        marketingConsent,
        socialUserDataResp.success
      );

      if (socialUserDataResp.success) {
        /* c8 ignore next */
        googleTracking?.trackSocialLogIn(
          socialSite,
          socialUserDataResp.data?.accountWasCreated as unknown as boolean,
          socialUserDataResp.data?.accountWasLinked as unknown as boolean
        );
        /* c8 ignore next */
        nr?.logSocialLogIn(
          socialSite,
          socialUserDataResp.data?.accountWasCreated as unknown as boolean,
          socialUserDataResp.data?.accountWasLinked as unknown as boolean
        );
      } else {
        const translatedError = translateMessage(
          {
            id: LABEL_KEYS['media-ui-login_errorSigningInWithSocial'],
            defaultMessage:
              defaultMessages['media-ui-login_errorSigningInWithSocial'],
          },
          { socialSite }
        );

        setError({ ...error, social: translatedError });

        /* c8 ignore next */
        googleTracking?.trackErrorEvent(`error_${socialSite}`);
        /* c8 ignore next */
        nr?.logErrorEvent('MediaUiLoginErrors', {
          errorMessage:
            socialUserDataResp.error?.errorMessage ||
            defaultMessages['media-ui-login_errorSigningInWithSocial'],
          email: (socialUserDataResp.data?.email as string) || '',
          type: 'SOCIAL',
        });
      }

      setLoginResp({
        ...socialUserDataResp,
        data: {
          ...socialUserDataResp.data,
          redirectToPostLoginConversion: shouldRedirectToPostLoginConversion(
            (regUIClient.getSubscriberData()?.subscriptionsData || []).length >
              0,
            // if account was not created, then it's a returning user
            !socialUserDataResp.data.accountWasCreated,
            postLoginConversionEnabled
          ),
        },
      });
      setIsLoggedIn(socialUserDataResp.success);
    };

    const onEmailSubmit = (
      email: string,
      optIn: boolean,
      success?: boolean
    ) => {
      // we can't tell if this is login or register anymore, so default to register
      /* c8 ignore next */
      googleTracking?.trackRegisterEvent('submit_email');
      /* c8 ignore next */
      nr?.logRegisterEvent('submit_email');

      onEmailCapture?.(email, optIn, success);
    };

    const onPasswordLogin = async (
      email: string,
      password: string,
      captcha?: CaptchaType
    ): Promise<void> => {
      if (!regUIClient) {
        return;
      }
      /* c8 ignore next */
      googleTracking?.trackClickEvent(`bconnect`);
      /* c8 ignore next */
      nr?.logClickEvent(`bconnect`);

      const bconnectUserDataResp = await onBconnectSignIn(
        regUIClient,
        email,
        password,
        captcha
      );

      onEmailCapture?.(
        (bconnectUserDataResp.data?.email as string) || '',
        marketingConsent,
        bconnectUserDataResp.success
      );

      if (bconnectUserDataResp.success) {
        /* c8 ignore next */
        googleTracking?.trackLoginEvent('bconnect_success');
        /* c8 ignore next */
        nr?.logLoginEvent('bconnect_success', {
          excessSessionsDeleted:
            bconnectUserDataResp.data?.excessSessionsDeleted,
          requiresPasswordUpdate:
            bconnectUserDataResp.data?.requiresPasswordUpdate,
        });
      } else {
        const translatedError = translateMessage({
          id: LABEL_KEYS['media-ui-login_failedToAuthenticate'],
          defaultMessage:
            defaultMessages['media-ui-login_failedToAuthenticate'],
        });

        setError({ ...error, password: translatedError });

        /* c8 ignore next */
        googleTracking?.trackErrorEvent(`error_bconnect`);

        nr?.logErrorEvent('MediaUiLoginErrors', {
          errorMessage:
            bconnectUserDataResp.error?.errorMessage ||
            defaultMessages['media-ui-login_failedToAuthenticate'],
          email: (bconnectUserDataResp.data?.email as string) || '',
          type: 'PASSWORD',
        });
      }

      setLoginResp({
        ...bconnectUserDataResp,
        data: {
          ...bconnectUserDataResp.data,
          redirectToPostLoginConversion: shouldRedirectToPostLoginConversion(
            (regUIClient.getSubscriberData()?.subscriptionsData || []).length >
              0,
            // if account was not created, then it's a returning user
            !bconnectUserDataResp.data.accountWasCreated,
            postLoginConversionEnabled
          ),
        },
      });
      setIsLoggedIn(bconnectUserDataResp.success);
    };

    const translatedContinueWithSocialText = (
      socialSite: SupportedSocialsType
    ) => {
      switch (socialSite) {
        case SOCIAL_SITES.GOOGLE:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_continueWithGoogle'],
            defaultMessage:
              defaultMessages['media-ui-login_continueWithGoogle'],
          });
        case SOCIAL_SITES.APPLE:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_continueWithApple'],
            defaultMessage: defaultMessages['media-ui-login_continueWithApple'],
          });
        case SOCIAL_SITES.FACEBOOK:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_continueWithFacebook'],
            defaultMessage:
              defaultMessages['media-ui-login_continueWithFacebook'],
          });
        case SOCIAL_SITES.TWITTER:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_continueWithTwitter'],
            defaultMessage:
              defaultMessages['media-ui-login_continueWithTwitter'],
          });
        case SOCIAL_SITES.TERMINAL:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_continueWithBba'],
            defaultMessage: defaultMessages['media-ui-login_continueWithBba'],
          });
        /* c8 ignore next */
        default:
          return '';
      }
    };

    const translatedSocialSignInText = (socialSite: SupportedSocialsType) => {
      switch (socialSite) {
        case SOCIAL_SITES.GOOGLE:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_googleSignInTitle'],
            defaultMessage: defaultMessages['media-ui-login_googleSignInTitle'],
          });
        case SOCIAL_SITES.APPLE:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_appleSignInTitle'],
            defaultMessage: defaultMessages['media-ui-login_appleSignInTitle'],
          });
        case SOCIAL_SITES.FACEBOOK:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_facebookSignInTitle'],
            defaultMessage:
              defaultMessages['media-ui-login_facebookSignInTitle'],
          });
        case SOCIAL_SITES.TWITTER:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_twitterSignInTitle'],
            defaultMessage:
              defaultMessages['media-ui-login_twitterSignInTitle'],
          });
        case SOCIAL_SITES.TERMINAL:
          return translateMessage({
            id: LABEL_KEYS['media-ui-login_bbaSignInTitle'],
            defaultMessage: defaultMessages['media-ui-login_bbaSignInTitle'],
          });
        /* c8 ignore next */
        default:
          return '';
      }
    };

    const socialSigninsComp =
      !showSocialSignins || socialLoginsList.length === 0 ? (
        <></>
      ) : (
        <div className={classes([styles.socialButtonsContainer])}>
          <OrSeparator theme={theme} />
          {socialLoginsList.map((socialSite: SupportedSocialsType) => {
            switch (socialSite) {
              case SOCIAL_SITES.GOOGLE:
                return (
                  <div className={styles.iconButton} key={socialSite}>
                    <GoogleSignInButton
                      onGoogleSignIn={googleSignInFunc}
                      buttonType="outlined"
                      buttonText={translatedContinueWithSocialText(
                        SOCIAL_SITES.GOOGLE as SupportedSocialsType
                      )}
                      onClickCb={() => {
                        /* c8 ignore next */
                        googleTracking?.trackClickEvent(
                          `connect_${SOCIAL_SITES.GOOGLE}`
                        );
                        /* c8 ignore next */
                        nr?.logClickEvent(`connect_${SOCIAL_SITES.GOOGLE}`);
                        onClickForGoogle?.();
                      }}
                      className={styles.googleButton}
                      theme={theme}
                      size="default"
                    />
                  </div>
                );
              case SOCIAL_SITES.APPLE:
              case SOCIAL_SITES.FACEBOOK:
              case SOCIAL_SITES.TWITTER:
              case SOCIAL_SITES.TERMINAL:
                return (
                  <div className={styles.iconButton} key={socialSite}>
                    <OutlinedButton
                      version="phoenix"
                      colorMode={theme === 'dark' ? 'dark' : 'light'}
                      iconPosition="left"
                      Icon={mapSocialsToIcons[socialSite]}
                      onClick={() => socialSignInFunc(socialSite)}
                      size="default"
                      fullWidth={true}
                      title={translatedSocialSignInText(socialSite)}
                    >
                      {translatedContinueWithSocialText(socialSite)}
                    </OutlinedButton>
                  </div>
                );
              /* c8 ignore next */
              default:
                // eslint-disable-next-line no-console
                /* c8 ignore next */ console.log(
                  'Encountered unknown social site',
                  socialSite
                );

                /* c8 ignore next */
                return <></>;
            }
          })}
        </div>
      );

    const gamesSubHeader = game ? (
      <div>
        <Suspense fallback={<div className={styles.lottie} />}>
          <LazyDotLottie
            className={styles.lottie}
            src={`${GAMES_ASSET_BASE_URL}/${
              game === 'alphadots'
                ? 'alphadotsRegwallAnimation.json'
                : 'pointedRegwallAnimation.json'
            }`}
            loop={game === 'pointed'}
            autoplay={true}
          />
        </Suspense>
        <div className={styles.lottieSubtext}>
          Get daily quizzes, game archives, and more.
        </div>
      </div>
    ) : null;

    return isLoggedIn ? (
      <></>
    ) : (
      <div
        className={classes([
          styles.content,
          {
            [styles.noPaddingTop]: !showTitle,
          },
        ])}
      >
        <EmailVerificationForm
          onEmailSubmit={onEmailSubmit}
          onTerminalLogin={onTerminalLogin}
          countryCode={countryCode}
          allowEditEmail={allowEditEmail}
          buttonText={continueButtonText}
          header={
            showTitle ? (
              <div>
                <h1
                  className={classes([
                    styles.title,
                    {
                      [styles.textWhite]: theme === 'dark',
                    },
                  ])}
                >
                  {formTitle}
                </h1>
                {gamesSubHeader}
              </div>
            ) : null
          }
          ref={ref}
          centeredLayout={false}
          autoFocus={emailFocus}
          marketingConsent={marketingConsent}
          inlineButton={inlineContinueButton}
          theme={theme}
          buttonType="primary"
          onPasswordLogin={onPasswordLogin}
          onVerificationCodeSubmit={emailSignInFunc}
          setGlobalMarketingConsent={setMarketingConsent}
          setShowSocialSignins={setShowSocialSignins}
          setIsLoggedIn={setIsLoggedIn}
          googleTracking={googleTracking}
          nr={nr}
          error={error}
          setError={setError}
          regUIClient={regUIClient}
          fullWidth={false}
          allowPassword={allowPassword}
          leadSource={leadSource}
        />
        {socialSigninsComp}
      </div>
    );
  }
);
